/**
 * OutlinePanel - a "shapes tree" side panel listing every node (icon + label)
 * and a section for edges. Clicking an entry selects it and centers the camera
 * on it. A search box filters by label (case-insensitive); pressing Enter
 * selects + centers the first match.
 */

import { useMemo, useState } from 'react'
import type { Node, Edge, ReactFlowInstance } from '@xyflow/react'
import { Search, Box, Spline, X } from 'lucide-react'
import './OutlinePanel.css'

interface OutlinePanelProps {
  nodes: Node[]
  edges: Edge[]
  reactFlow: ReactFlowInstance
  onSelectNode: (id: string) => void
  onSelectEdge?: (id: string) => void
  onClose?: () => void
}

function nodeLabel(node: Node): string {
  const data = node.data as any
  return (
    (typeof data?.label === 'string' && data.label) ||
    (typeof data?.shapeId === 'string' && data.shapeId) ||
    node.id
  )
}

function nodeIcon(node: Node) {
  const data = node.data as any
  const icon = data?.icon
  if (typeof icon === 'string' && (icon.startsWith('/icons/') || icon.startsWith('/shapes/'))) {
    return <img src={icon} alt="" className="outline-row-img" />
  }
  if (typeof icon === 'string' && icon.length <= 4) {
    return <span className="outline-row-emoji">{icon}</span>
  }
  return <Box size={14} />
}

function edgeLabel(edge: Edge): string {
  const lbl = typeof edge.label === 'string' ? edge.label : ''
  return lbl || `${edge.source} → ${edge.target}`
}

export default function OutlinePanel({
  nodes,
  edges,
  reactFlow,
  onSelectNode,
  onSelectEdge,
  onClose,
}: OutlinePanelProps) {
  const [query, setQuery] = useState('')

  const q = query.trim().toLowerCase()

  const filteredNodes = useMemo(() => {
    if (!q) return nodes
    return nodes.filter((n) => nodeLabel(n).toLowerCase().includes(q))
  }, [nodes, q])

  const filteredEdges = useMemo(() => {
    if (!q) return edges
    return edges.filter((e) => edgeLabel(e).toLowerCase().includes(q))
  }, [edges, q])

  const centerOnNode = (id: string) => {
    onSelectNode(id)
    reactFlow.fitView({ nodes: [{ id }], duration: 300, maxZoom: 1.5 })
  }

  const centerOnEdge = (edge: Edge) => {
    onSelectEdge?.(edge.id)
    // Center on the edge's two endpoints so the whole edge is in view.
    reactFlow.fitView({
      nodes: [{ id: edge.source }, { id: edge.target }],
      duration: 300,
    })
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (filteredNodes.length > 0) {
        centerOnNode(filteredNodes[0].id)
      } else if (filteredEdges.length > 0) {
        centerOnEdge(filteredEdges[0])
      }
    }
  }

  return (
    <div className="outline-panel">
      <div className="panel-header">
        <h3>Outline</h3>
        {onClose && (
          <button onClick={onClose} className="close-btn" title="Close outline">
            ×
          </button>
        )}
      </div>

      <div className="outline-search">
        <Search size={14} className="outline-search-icon" />
        <input
          type="text"
          value={query}
          placeholder="Find by label…"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          className="outline-search-input"
        />
        {query && (
          <button
            className="outline-search-clear"
            onClick={() => setQuery('')}
            title="Clear"
          >
            <X size={12} />
          </button>
        )}
      </div>

      <div className="outline-content">
        <div className="outline-section-title">
          Nodes <span className="outline-count">{filteredNodes.length}</span>
        </div>
        {filteredNodes.length === 0 && (
          <div className="outline-empty">No matching nodes</div>
        )}
        {filteredNodes.map((node) => (
          <button
            key={node.id}
            className="outline-row"
            onClick={() => centerOnNode(node.id)}
            title={nodeLabel(node)}
          >
            <span className="outline-row-icon">{nodeIcon(node)}</span>
            <span className="outline-row-label">{nodeLabel(node)}</span>
          </button>
        ))}

        {edges.length > 0 && (
          <>
            <div className="outline-section-title">
              Edges <span className="outline-count">{filteredEdges.length}</span>
            </div>
            {filteredEdges.length === 0 && (
              <div className="outline-empty">No matching edges</div>
            )}
            {filteredEdges.map((edge) => (
              <button
                key={edge.id}
                className="outline-row"
                onClick={() => centerOnEdge(edge)}
                title={edgeLabel(edge)}
              >
                <span className="outline-row-icon">
                  <Spline size={14} />
                </span>
                <span className="outline-row-label">{edgeLabel(edge)}</span>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  )
}
